import { debugLogs } from "./debugLogs";
function readDebugLogsOverride() {
    try {
        const raw = window.localStorage.getItem("node-one.prefs.v1");
        if (!raw)
            return "default";
        const v = JSON.parse(raw).debugLogs;
        if (v === "enable" || v === "disable")
            return v;
    }
    catch { }
    return "default";
}
export class ApiClientError extends Error {
    status;
    code;
    details;
    constructor(message, status, code, details) {
        super(message);
        this.name = "ApiClientError";
        this.status = status;
        this.code = code;
        this.details = details;
    }
}
async function request(path, init = {}) {
    const headers = new Headers(init.headers);
    if (init.body !== undefined && !headers.has("content-type")) {
        headers.set("content-type", "application/json");
    }
    const debugOverride = readDebugLogsOverride();
    if (debugOverride === "enable")
        headers.set("x-debug-logs", "1");
    else if (debugOverride === "disable")
        headers.set("x-debug-logs", "0");
    const response = await fetch(path, {
        ...init,
        credentials: "include",
        headers,
    });
    const debugHeader = response.headers.get("x-debug-log");
    if (debugHeader) {
        try {
            const parsed = JSON.parse(debugHeader);
            debugLogs.push({ ...parsed, receivedAt: Date.now() });
        }
        catch {
            // ignore malformed header
        }
    }
    if (response.status === 204)
        return undefined;
    const text = await response.text();
    const body = text ? JSON.parse(text) : null;
    if (!response.ok) {
        const error = body?.error;
        throw new ApiClientError(error?.message ?? `Request failed with status ${response.status}.`, response.status, error?.code, error?.details);
    }
    return body;
}
function jsonBody(body) {
    return { body: JSON.stringify(body) };
}
export const api = {
    me: () => request("/api/auth/me"),
    signin: (body) => request("/api/auth/signin", {
        method: "POST",
        ...jsonBody(body),
    }),
    signup: (body) => request("/api/auth/signup", {
        method: "POST",
        ...jsonBody(body),
    }),
    signout: () => request("/api/auth/signout", { method: "POST" }),
    listReadmes: () => request("/api/readmes"),
    createReadme: (body) => request("/api/readmes", {
        method: "POST",
        ...jsonBody(body),
    }),
    getReadme: (id) => request(`/api/readmes/${encodeURIComponent(id)}`),
    updateReadme: (id, body) => request(`/api/readmes/${encodeURIComponent(id)}`, {
        method: "PATCH",
        ...jsonBody(body),
    }),
    deleteReadme: (id) => request(`/api/readmes/${encodeURIComponent(id)}`, {
        method: "DELETE",
    }),
    listRevisions: (id) => request(`/api/readmes/${encodeURIComponent(id)}/revisions`),
    importReadmes: (body) => request("/api/readmes/import", {
        method: "POST",
        ...jsonBody(body),
    }),
    suggestTags: (body) => request("/api/readmes/suggest-tags", {
        method: "POST",
        ...jsonBody(body),
    }),
    suggestTitle: (body) => request("/api/readmes/suggest-title", {
        method: "POST",
        ...jsonBody(body),
    }),
    suggestMeta: (body) => request("/api/readmes/suggest-meta", {
        method: "POST",
        ...jsonBody(body),
    }),
    getAiHealth: () => request("/api/ai/health"),
    listUsers: () => request("/api/users"),
    updateUser: (id, body) => request(`/api/users/${encodeURIComponent(id)}`, {
        method: "PATCH",
        ...jsonBody(body),
    }),
    deleteUser: (id, body) => request(`/api/users/${encodeURIComponent(id)}`, {
        method: "DELETE",
        ...jsonBody(body ?? {}),
    }),
    getUserPlanParticipants: (id) => request(`/api/users/${encodeURIComponent(id)}/plan-participants`),
    listSkills: () => request("/api/skills"),
    createSkill: (body) => request("/api/skills", {
        method: "POST",
        ...jsonBody(body),
    }),
    getSkill: (id) => request(`/api/skills/${encodeURIComponent(id)}`),
    updateSkill: (id, body) => request(`/api/skills/${encodeURIComponent(id)}`, {
        method: "PATCH",
        ...jsonBody(body),
    }),
    deleteSkill: (id) => request(`/api/skills/${encodeURIComponent(id)}`, { method: "DELETE" }),
    importSkill: (body) => request("/api/skills/import", {
        method: "POST",
        ...jsonBody(body),
    }),
    listShares: () => request("/api/shares"),
    createShare: (body) => request("/api/shares", {
        method: "POST",
        ...jsonBody(body),
    }),
    deleteShare: (id) => request(`/api/shares/${encodeURIComponent(id)}`, { method: "DELETE" }),
    getPublicContent: (token) => request(`/api/public/${encodeURIComponent(token)}`),
    listApiKeys: () => request("/api/api-keys"),
    createApiKey: () => request("/api/api-keys", { method: "POST" }),
    revokeApiKey: (id) => request(`/api/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" }),
    listSharedPlans: () => request("/api/shared-plans"),
    createSharedPlan: (body) => request("/api/shared-plans", {
        method: "POST",
        ...jsonBody(body),
    }),
    getSharedPlan: (token, sessionKey, displayName) => {
        const params = new URLSearchParams();
        if (sessionKey)
            params.set("sessionKey", sessionKey);
        if (displayName)
            params.set("displayName", displayName);
        const qs = params.size ? `?${params.toString()}` : "";
        return request(`/api/shared-plans/${encodeURIComponent(token)}${qs}`);
    },
    updateSharedPlan: (token, body) => request(`/api/shared-plans/${encodeURIComponent(token)}`, { method: "PATCH", ...jsonBody(body) }),
    deleteSharedPlan: (token) => request(`/api/shared-plans/${encodeURIComponent(token)}`, {
        method: "DELETE",
    }),
    listSharedPlanRevisions: (token) => request(`/api/shared-plans/${encodeURIComponent(token)}/revisions`),
    getSharedPlanRevision: (token, revId) => request(`/api/shared-plans/${encodeURIComponent(token)}/revisions?id=${encodeURIComponent(revId)}`),
    listSharedPlanActivity: (token, limit) => request(`/api/shared-plans/${encodeURIComponent(token)}/activity${limit ? `?limit=${limit}` : ""}`),
    bootstrapSharedPlan: (token) => request(`/api/shared-plans/${encodeURIComponent(token)}/bootstrap`),
    joinSharedPlan: (token, body) => request(`/api/shared-plans/${encodeURIComponent(token)}/join`, { method: "POST", ...jsonBody(body) }),
    listSharedPlanParticipants: (token) => request(`/api/shared-plans/${encodeURIComponent(token)}/participants`),
    removeSharedPlanParticipant: (token, participantId, body) => request(`/api/shared-plans/${encodeURIComponent(token)}/participants/${encodeURIComponent(participantId)}`, { method: "DELETE", ...jsonBody(body) }),
    getCollabHealth: () => request("/api/collab/health"),
};
